import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { usePacientes } from '../hooks/usePacientes';
import { useConsultas } from '../hooks/useConsultas';
import { useAlertas } from '../hooks/useAlertas';
import { useFinancial } from '../hooks/useFinancial';
import { usePlanos } from '../hooks/usePlanos';
import { useChat } from '../hooks/useChat';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getConversasChat } from '../services/dataService';
import { CreateConsultationModal } from '../components/consultation/CreateConsultationModal';
import { ConsultationDetailModal } from '../components/consultation/ConsultationDetailModal';
import { Consulta } from '../services/mockData';
import { ref, get } from '../services/phpDataAdapter';
import { database } from '../config/backend';
import './Pages.css';

function formatHora(iso: string) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface PatientAdherence {
  id: string;
  nome: string;
  adherence: number;
}

interface RecentMeasurement {
  pacienteNome: string;
  pacienteId: string;
  weight?: number;
  date: string;
}

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { pacientes, loading: loadingPacientes } = usePacientes();
  const { consultas, loading: loadingConsultas } = useConsultas(7);
  const { alertas, loading: loadingAlertas } = useAlertas();
  const { stats, loadStats } = useFinancial();
  const { planos, loading: loadingPlanos } = usePlanos();
  const { loading: loadingChat } = useChat(null);
  const navigate = useNavigate();

  // Modals state
  const [modalConsultOpen, setModalConsultOpen] = useState(false);
  const [modalConsultDetailOpen, setModalConsultDetailOpen] = useState(false);
  const [selectedConsulta, setSelectedConsulta] = useState<Consulta | null>(null);


  // Adherence state
  const [patientAdherence, setPatientAdherence] = useState<PatientAdherence[]>([]);
  const [loadingAdherence, setLoadingAdherence] = useState(true);
  
  // Additional stats state
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [recentMeasurements, setRecentMeasurements] = useState<RecentMeasurement[]>([]);
  const [loadingMeasurements, setLoadingMeasurements] = useState(false);

  const pacientesAtivos = pacientes.filter((p) => p.status === 'ativo').length;
  const pacientesAlerta = alertas.length;
  const totalPacientes = pacientes.length;
  
  // Calcular novos pacientes deste mês
  const hoje = new Date();
  const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  const novosPacientesMes = pacientes.filter((p) => {
    if (!p.ultimaConsulta) return false;
    const dataConsulta = new Date(p.ultimaConsulta);
    return dataConsulta >= inicioMes;
  }).length;
  
  // Estatísticas financeiras
  const receitaMes = stats?.receitaTotal || 0;
  const receitaPendente = consultas
    .filter((c) => c.valor && c.pago !== true)
    .reduce((sum, c) => sum + (c.valor || 0), 0);
  
  // Planos ativos
  const planosAtivos = planos.filter((p) => {
    const dataAtualizacao = new Date(p.atualizadoEm);
    const semanasAtras = (hoje.getTime() - dataAtualizacao.getTime()) / (1000 * 60 * 60 * 24 * 7);
    return semanasAtras <= p.duracaoSemanas;
  }).length;

  const consultasHoje = consultas.filter((c) => {
    const d = new Date(c.dataIso);
    return (
      d.getFullYear() === hoje.getFullYear() &&
      d.getMonth() === hoje.getMonth() &&
      d.getDate() === hoje.getDate()
    );
  });

  const proximasConsultas = consultas.slice(0, 5);

  // Função auxiliar para gerar chave de data
  function getDateKey(date: Date = new Date()): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Carregar estatísticas financeiras
  useEffect(() => {
    if (user?.uid) {
      const hoje = new Date();
      loadStats(hoje.getMonth() + 1, hoje.getFullYear(), true);
    }
  }, [user?.uid, loadStats]);

  // Buscar mensagens não lidas
  useEffect(() => {
    async function loadUnreadMessages() {
      if (!user?.uid) return;
      
      try {
        const conversasList = await getConversasChat(user.uid);
        // Contar conversas com mensagens recentes não lidas
        // Por enquanto, vamos contar todas as conversas como potencialmente não lidas
        // Em uma implementação completa, você verificaria o campo lastReadTimestamp
        setUnreadMessagesCount(conversasList.length);
      } catch (error) {
        console.error('Erro ao carregar mensagens não lidas:', error);
      }
    }
    
    if (!loadingChat && user?.uid) {
      loadUnreadMessages();
    }
  }, [user?.uid, loadingChat]);

  // Buscar medições recentes
  useEffect(() => {
    async function loadRecentMeasurements() {
      if (!user?.uid || pacientes.length === 0) {
        setLoadingMeasurements(false);
        return;
      }

      setLoadingMeasurements(true);
      try {
        const measurementsPromises = pacientes.slice(0, 5).map(async (paciente) => {
          try {
            const measurementsRef = ref(database, `users/${paciente.id}/measurements`);
            const measurementsSnap = await get(measurementsRef);
            
            if (measurementsSnap.exists()) {
              const measurements = measurementsSnap.val() as Record<string, any>;
              const measurementsList = Object.values(measurements) as any[];
              
              if (measurementsList.length > 0) {
                measurementsList.sort((a, b) => {
                  const dateA = new Date(a.date || a.timestamp || 0).getTime();
                  const dateB = new Date(b.date || b.timestamp || 0).getTime();
                  return dateB - dateA;
                });
                
                const latest = measurementsList[0];
                const measurement: RecentMeasurement = {
                  pacienteNome: paciente.nome,
                  pacienteId: paciente.id,
                  weight: latest.weight,
                  date: latest.date || latest.timestamp || new Date().toISOString(),
                };
                return measurement;
              }
            }
            return null;
          } catch {
            return null;
          }
        });

        const measurementsData = await Promise.all(measurementsPromises);
        const validMeasurements: RecentMeasurement[] = measurementsData.filter(
          (m): m is RecentMeasurement => m !== null && m !== undefined
        );
        setRecentMeasurements(validMeasurements);
      } catch (error) {
        console.error('Erro ao carregar medições:', error);
        setRecentMeasurements([]);
      } finally {
        setLoadingMeasurements(false);
      }
    }

    if (!loadingPacientes) {
      loadRecentMeasurements();
    }
  }, [user?.uid, pacientes, loadingPacientes]);

  // Buscar adesão dos pacientes
  useEffect(() => {
    async function loadAdherence() {
      if (!user?.uid || pacientes.length === 0) {
        setLoadingAdherence(false);
        return;
      }

      setLoadingAdherence(true);
      try {
        const todayKey = getDateKey();
        const adherencePromises = pacientes.map(async (paciente) => {
          try {
            const scoreSnap = await get(
              ref(database, `users/${paciente.id}/dietTracking/${todayKey}/dailyScore`)
            );
            const score = scoreSnap.exists() ? Number(scoreSnap.val()) || 0 : 0;
            return {
              id: paciente.id,
              nome: paciente.nome,
              adherence: score,
            };
          } catch {
            return {
              id: paciente.id,
              nome: paciente.nome,
              adherence: 0,
            };
          }
        });

        const adherenceData = await Promise.all(adherencePromises);
        setPatientAdherence(adherenceData);
      } catch (error) {
        console.error('Erro ao carregar adesão:', error);
        setPatientAdherence([]);
      } finally {
        setLoadingAdherence(false);
      }
    }

    if (!loadingPacientes) {
      loadAdherence();
    }
  }, [user?.uid, pacientes, loadingPacientes]);

  const handleCardClick = (route: string) => {
    navigate(route);
  };

  const handleConsultaClick = (consulta: Consulta) => {
    setSelectedConsulta(consulta);
    setModalConsultDetailOpen(true);
  };

  const handleConsultationSuccess = () => {
    window.location.reload();
  };

  return (
    <div className="ns-page ns-page-dashboard">
      <div className="ns-page-header">
        <div>
          <h2>Visão geral</h2>
          <p>
            Resumo rápido dos seus pacientes, consultas e alertas
            importantes.
          </p>
        </div>
        <div className="ns-page-header-actions">
          <Button variant="secondary" onClick={() => navigate('/pacientes?novo=1')}>
            Novo paciente
          </Button>
          <Button onClick={() => setModalConsultOpen(true)}>Nova consulta</Button>
        </div>
      </div>

      <div className="ns-grid ns-grid-3">
        <Card 
          title="Pacientes ativos" 
          subtitle="Acompanhamento em andamento"
          onClick={() => handleCardClick('/pacientes')}
          style={{ cursor: 'pointer' }}
        >
          <div className="ns-kpi">
            <span className="ns-kpi-value">
              {loadingPacientes ? '...' : pacientesAtivos}
            </span>
          </div>
          {!loadingPacientes && totalPacientes > 0 && (
            <div style={{ marginTop: '8px', fontSize: '0.875rem', color: 'var(--ns-text-secondary)' }}>
              {totalPacientes} total • {novosPacientesMes} novos este mês
            </div>
          )}
        </Card>

        <Card 
          title="Consultas de hoje" 
          subtitle="Presenciais + online"
          onClick={() => handleCardClick('/agenda')}
          style={{ cursor: 'pointer' }}
        >
          <div className="ns-kpi">
            <span className="ns-kpi-value">
              {loadingConsultas ? '...' : consultasHoje.length}
            </span>
          </div>
        </Card>

        <Card 
          title="Alertas" 
          subtitle="Pacientes com baixa adesão"
          onClick={() => handleCardClick('/adesoes')}
          style={{ cursor: 'pointer' }}
        >
          <div className="ns-kpi">
            <span className="ns-kpi-value">
              {loadingAlertas ? '...' : pacientesAlerta}
            </span>
          </div>
        </Card>
      </div>

      <div className="ns-grid ns-grid-3" style={{ marginTop: '1.5rem' }}>
        <Card 
          title="Receita do mês" 
          subtitle="Total recebido este mês"
          onClick={() => handleCardClick('/financeiro')}
          style={{ cursor: 'pointer' }}
        >
          <div className="ns-kpi">
            <span className="ns-kpi-value" style={{ fontSize: '1.5rem' }}>
              {stats ? `R$ ${receitaMes.toFixed(2).replace('.', ',')}` : '...'}
            </span>
          </div>
        </Card>

        <Card 
          title="Receita pendente" 
          subtitle="Consultas aguardando pagamento"
          onClick={() => handleCardClick('/financeiro')}
          style={{ cursor: 'pointer' }}
        >
          <div className="ns-kpi">
            <span className="ns-kpi-value" style={{ fontSize: '1.5rem', color: receitaPendente > 0 ? '#f59e0b' : 'inherit' }}>
              R$ {receitaPendente.toFixed(2).replace('.', ',')}
            </span>
          </div>
        </Card>

        <Card 
          title="Planos ativos" 
          subtitle="Planos de refeição em andamento"
          onClick={() => handleCardClick('/planos')}
          style={{ cursor: 'pointer' }}
        >
          <div className="ns-kpi">
            <span className="ns-kpi-value">
              {loadingPlanos ? '...' : planosAtivos}
            </span>
          </div>
          {!loadingPlanos && planos.length > 0 && (
            <div style={{ marginTop: '8px', fontSize: '0.875rem', color: 'var(--ns-text-secondary)' }}>
              {planos.length} plano(s) cadastrado(s)
            </div>
          )}
        </Card>
      </div>

      {unreadMessagesCount > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <Card 
            title="Mensagens não lidas" 
            subtitle={`${unreadMessagesCount} conversa(s) com novas mensagens`}
            onClick={() => handleCardClick('/chat')}
            style={{ cursor: 'pointer', borderLeft: '4px solid #3b82f6' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '1.25rem' }}>💬</span>
              <span>Clique para ver as conversas</span>
            </div>
          </Card>
        </div>
      )}

      <div className="ns-grid ns-grid-2">
        <Card
          title="Próximas consultas"
          subtitle="Hoje e próximos 7 dias"
          actions={
            <Button variant="ghost" onClick={() => handleCardClick('/agenda')}>
              Ver agenda completa
            </Button>
          }
        >
          <div className="ns-table-wrapper">
            <table className="ns-table">
            <thead>
              <tr>
                <th>Horário</th>
                <th>Paciente</th>
                <th>Tipo</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loadingConsultas && (
                <tr>
                  <td colSpan={4}>Carregando consultas...</td>
                </tr>
              )}
              {!loadingConsultas && proximasConsultas.length === 0 && (
                <tr>
                  <td colSpan={4}>Nenhuma consulta nos próximos dias.</td>
                </tr>
              )}
              {!loadingConsultas &&
                proximasConsultas.map((c) => (
                  <tr 
                    key={c.id}
                    onClick={() => handleConsultaClick(c)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{formatHora(c.dataIso)}</td>
                    <td>{c.pacienteNome}</td>
                    <td>
                      {c.tipo === 'primeira' ? 'Primeira consulta' : 'Retorno'}
                    </td>
                    <td>
                      <span
                        className={
                          c.status === 'confirmada'
                            ? 'ns-badge ns-badge-success'
                            : c.status === 'aguardando'
                              ? 'ns-badge ns-badge-warning'
                              : 'ns-badge ns-badge-neutral'
                        }
                      >
                        {c.status === 'confirmada'
                          ? 'Confirmada'
                          : c.status === 'aguardando'
                            ? 'Aguardando'
                            : 'Em aberto'}
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          </div>
        </Card>

        <Card
          title="Adesão aos planos"
          subtitle="Resumo simples baseado nos dados atuais"
        >
          {loadingAdherence ? (
            <div className="ns-chart-placeholder">
              <p className="ns-chart-caption">Carregando adesão...</p>
            </div>
          ) : patientAdherence.length === 0 ? (
            <div className="ns-chart-placeholder">
              <p className="ns-chart-caption">
                Nenhum dado de adesão disponível no momento.
              </p>
            </div>
          ) : (
            <div className="ns-adherence-list">
              {patientAdherence
                .sort((a, b) => b.adherence - a.adherence)
                .slice(0, 5)
                .map((patient) => (
                  <div key={patient.id} className="ns-adherence-item">
                    <div className="ns-adherence-info">
                      <span className="ns-adherence-name">{patient.nome}</span>
                      <span className="ns-adherence-percent">{patient.adherence}%</span>
                    </div>
                    <div className="ns-adherence-bar">
                      <div
                        className="ns-adherence-bar-fill"
                        style={{
                          width: `${patient.adherence}%`,
                          backgroundColor:
                            patient.adherence >= 80
                              ? '#10b981'
                              : patient.adherence >= 50
                                ? '#f59e0b'
                                : '#ef4444',
                        }}
                      />
                    </div>
                  </div>
                ))}
              {patientAdherence.length > 5 && (
                <div style={{ marginTop: '12px', textAlign: 'center' }}>
                  <Button
                    variant="ghost"
                    onClick={() => handleCardClick('/adesoes')}
                  >
                    Ver todos ({patientAdherence.length})
                  </Button>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>

      {recentMeasurements.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <Card
            title="Medições recentes"
            subtitle="Últimas medições registradas pelos pacientes"
            actions={
              <Button variant="ghost" onClick={() => handleCardClick('/medicoes')}>
                Ver todas as medições
              </Button>
            }
          >
            <div className="ns-table-wrapper">
              <table className="ns-table">
                <thead>
                  <tr>
                    <th>Paciente</th>
                    <th>Peso</th>
                    <th>Data</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingMeasurements && (
                    <tr>
                      <td colSpan={3}>Carregando medições...</td>
                    </tr>
                  )}
                  {!loadingMeasurements &&
                    recentMeasurements.slice(0, 5).map((m, idx) => (
                      <tr 
                        key={`${m.pacienteId}-${idx}`}
                        onClick={() => handleCardClick(`/medicoes?patient=${m.pacienteId}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>{m.pacienteNome}</td>
                        <td>{m.weight ? `${m.weight} kg` : '-'}</td>
                        <td>
                          {new Date(m.date).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* Modal Nova Consulta */}
      <CreateConsultationModal
        isOpen={modalConsultOpen}
        onClose={() => setModalConsultOpen(false)}
        onSuccess={handleConsultationSuccess}
      />

      {/* Modal Detalhes Consulta */}
      <ConsultationDetailModal
        isOpen={modalConsultDetailOpen}
        onClose={() => {
          setModalConsultDetailOpen(false);
          setSelectedConsulta(null);
        }}
        consulta={selectedConsulta}
      />
    </div>
  );
};

